<template>
  <div class="container flow">
    <fieldset>
      <div v-for="(rekening, key, index) in $v.overige.rekening.$each.$iter" :key="'rekInput-' + key">
        <iban-input
            v-model="rekening.$model.iban"
            :id="`rekening-iban-${key}`"
            :label="
              overige.rekening.length === 1
                ? labels.rekening.iban
                : labels.rekening.iban + ' ' + (index + 1)
            "
            :is-dirty="isDiffToCache(overige.rekening[key].iban, cache.rekening[key].iban)"
            :is-new="index >= cache.rekening.length"
            :validation-object="rekening.iban"
            :is-disabled="disabledFieldsMap.rekening"
            :info="infoForInput('rekening')"
            :show-delete="index !== 0"
            @delete-iban="overige.rekening.splice(index, 1)"
        >
        </iban-input>
        <bic-input
            v-model="rekening.$model.bic"
            :label="
              overige.rekening.length !== 1
                ? labels.rekening.bic + ' ' + (index + 1)
                : labels.rekening.bic
            "
            :id="`rekening-bic-${key}`"
            :is-dirty="isDiffToCache(overige.rekening[key], cache.rekening[key])"
            :validation-object="rekening.bic"
            :is-disabled="disabledFieldsMap.rekening"
        ></bic-input>
      </div>
      <yes-no-input
        v-if="!hiddenFieldsMap.rijbewijs"
        v-model="overige.mobiliteit.rijbewijs"
        id="pLidEditingRijbewijs"
        name="rijbewijsRadio"
        :label="labels.rijbewijs"
        :is-disabled="disabledFieldsMap.rijbewijs"
        :info="infoForInput('rijbewijs')"
        :is-dirty="overige.mobiliteit.rijbewijs !== cache.mobiliteit.rijbewijs"
      >
      </yes-no-input>
    </fieldset>    
    <fieldset>
      <h5>Nummerplaten</h5>
      <div class="flow" style="--flow-space: 2rem;">
        <div v-for="(nummerplaat, key, index) in $v.overige.mobiliteit.nummerplaten.$each.$iter" :key="`nummerplaat-${key}`">
          <text-input
            v-model="nummerplaat.$model.nummerplaat"
            :id="`nummerplaat-${key}`"
            :label="`${labels.nummerplaat} ${index + 1}`"
            :is-disabled="disabledFieldsMap.nummerplaten || nummerplaat.$model.id !== 0"
            :is-dirty="
              isDiffToCache(
                overige.mobiliteit.nummerplaten[parseInt(index)],
                cache.mobiliteit.nummerplaten[index]
              )
            "
            :validation-object="nummerplaat.nummerplaat"
          >
          </text-input>
          <div class="row" v-if="!disabledFieldsMap.nummerplaten">
            <div class="col-sm-3"></div>
            <div class="col-sm-9">
              <button @click="deleteNummerplaat(index)" type="button" class="btn btn-sm btn-outline-dark">
                <font-awesome-icon :icon="['far', 'trash-alt']" size="s"></font-awesome-icon>
                <span class="pr-1">Verwijderen</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </fieldset>
    <div class="text-center my-3 pb-3">
      <plus-button
        @click="addNummerplaat()"
        label="Nummerplaat toevoegen"
        :disabled="disabledFieldsMap.nummerplaten"
      >
      </plus-button>
    </div>
    <fieldset v-if="!hiddenFieldsMap.orcid">
      <text-input v-model="$v.overige.orcid.$model"
                  id="idForOrcid"
                  :maxLength="19"
                  :label="labels.orcid"
                  :is-dirty="overige.orcid !== cache.orcid"
                  :info="infoForInput('orcid')"
                  :is-disabled="disabledFieldsMap.orcid"
                  :validation-object="$v.overige.orcid">
      </text-input>
    </fieldset>
    <div class="pt-2 mt-3 border-top">
      <label class="font-weight-bold">Opmerking:</label>
      <textarea
        @input="onRemarkChange"
        v-model="currentRemark"
        rows="3"
        class="form-control mb-2"
        placeholder="Opmerking"
        type="text"
      ></textarea>
    </div>
    <file-upload
      label="Bewijsstuk(ken) uploaden:"
      :upload-percentage="0"
      @files-changed="onFilesChanged"
    ></file-upload>
  </div>
</template>

<script>
import diff from "deep-diff";
import { maxLength, required } from "vuelidate/lib/validators";
import DossierLabels from "./../DossierLabels.js";
import { ibanValidator } from "../../utils/IbanUtils";
import { bicValidator } from "../../utils/bic-validator";
import { proofSuppliedIfNeeded, } from "../../utils/DossierUtils.js";
import IbanInput from "./../../inputs/IbanInput.vue";
import YesNoInput from "./../../inputs/YesNoRadials.vue";
import FileUpload from "./../../inputs/FileUpload.vue";
import PlusButton from "../../interface/PlusButton.vue";
import PhoneInput from "../../inputs/PhoneInput.vue";
import TextInput from "../../inputs/TextInput.vue";
import BicInput from "../../inputs/BicInput.vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

export default {
  name: "overige-form",
  props: {
    overige: {
      type: Object,
    },
    cache: {
      type: Object,
    },
    remark: {
      type: String,
    },
    attachments: {
      type: Array,
      required: false,
    },
    disabledFieldsMap: {
      type: Object,
    },
    proofRequiredFieldsMap: {
      type: Object,
    },
    proofInfoFieldsMap: {
      type: Object,
    },
    hiddenFieldsMap: {
      type: Object,
      required: true,
    },
  },
  components: {
    PhoneInput,
    PlusButton,
    YesNoInput,
    IbanInput,
    FileUpload,
    TextInput,
    BicInput,
    FontAwesomeIcon,
  },
  data() {
    return {
      labels: DossierLabels.overige,
      currentRemark: this.remark,
      currentAttachments: this.attachments,
    };
  },
  methods: {
    addRekening() {
      this.overige.rekening.push({ id: 0, iban: "" });
    },
    addNummerplaat() {
      this.overige.mobiliteit.nummerplaten.push({
        id: 0,
        nummerplaat: ""
      });
    },
    deleteNummerplaat(index) {
      this.overige.mobiliteit.nummerplaten.splice(index, 1);
    },
    isDiffToCache(newValue, cachedValue) {
      let parsedValue = JSON.parse(JSON.stringify(newValue));
      return diff(cachedValue, parsedValue) !== undefined;
    },
    onFilesChanged(value) {
      this.currentAttachments = value;
      this.$emit("files-changed", this.currentAttachments);
    },
    onRemarkChange() {
      this.$emit("remark-changed", this.currentRemark);
    },
    isFieldUpdated(fieldName) {
      const parsedValue = JSON.parse(JSON.stringify(this.overige[fieldName]));
      return this.isDiffToCache(parsedValue, this.cache[fieldName]);
    },
    isMobiliteitFieldUpdated(fieldName) {
      const parsedValue = JSON.parse(JSON.stringify(this.overige.mobiliteit[fieldName]));
      return this.isDiffToCache(parsedValue, this.cache.mobiliteit[fieldName]);
    },
    infoForInput(valueName) {
      if (this.proofRequiredFieldsMap[valueName]) {
        return this.proofInfoFieldsMap[valueName] === ""
            ? "Bewijsstuk vereist om dit veld aan te passen."
            : this.proofInfoFieldsMap[valueName];
      } else {
        return null;
      }
    },
  },
  computed: {
    doesFormRequireProof() {
      return Object.values(this.proofRequiredFieldsMap).indexOf(true) > -1;
    },
  },
  validations() {
    return {
      overige: {
        rekening: {
          $each: {
            proofValidator: proofSuppliedIfNeeded(
              this.proofRequiredFieldsMap.rekening,
              this.isFieldUpdated("rekening"),
              this.currentAttachments
            ),
            iban: {
              required,
              ibanValidator,
            },
            bic: {
              bicValidator
            },
          },
        },        
        mobiliteit: {
          rijbewijs: {
            proofValidator: proofSuppliedIfNeeded(
              this.proofRequiredFieldsMap.rijbewijs,
              this.isMobiliteitFieldUpdated("rijbewijs"),
              this.currentAttachments
            ),
            required,
          },
          nummerplaten: {
            $each: {
              nummerplaat: {
                required,
                maxLength: maxLength(10),
              },
            },
          },

        },
        orcid: {},
      },
    };
  },
  watch: {
    $v: {
      handler(value) {
        this.$emit("validation-change", value.$invalid);
      },
      deep: true,
    },
  },
};
</script>
